import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import ApiState from "@/components/base/ApiState";
import CategoryTile from "@/pages/categories/components/CategoryTile";
import { getCategories } from "@/services/publicApi";
import { useApiResource } from "@/hooks/useApiResource";
import { OG_IMAGES } from "@/config/ogImages";

export default function Categories() {
  const { data, loading, error, reload } = useApiResource(() => getCategories(), []);
  const categories = data ?? [];

  return (
    <>
      <Seo
        title="All Business Categories"
        description="Explore every business category on Mysore Yellow Pages — restaurants, hotels, hospitals, builders, salons and home services across Mysore, Mandya, Chamarajanagar and Kodagu."
        keywords="business categories Mysore, local services list, directory categories Karnataka"
        path="/categories"
        image={OG_IMAGES.categories}
      />

      {/* Intro band */}
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-12">
          <Breadcrumbs items={[{ label: "Categories" }]} />
          <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
            All business categories
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            Explore every category on Mysore Yellow Pages — from restaurants and resorts to hospitals, builders, salons
            and home services across the Mysore region.
          </p>
          <Link
            to="/categories-a-z"
            className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors duration-200 hover:border-accent-300 hover:text-primary-700 cursor-pointer"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-sort-alphabet-asc text-base"></i>
            </span>
            View all categories A to Z
          </Link>
        </div>
      </section>

      {/* Category grid */}
      <section className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-12">
        <ApiState
          loading={loading}
          error={error}
          onRetry={reload}
          skeleton={
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className="h-64 animate-pulse rounded-lg border border-background-200 bg-background-100/60"
                ></div>
              ))}
            </div>
          }
          isEmpty={categories.length === 0}
          empty={<p className="text-sm text-foreground-600">No categories available right now.</p>}
        >
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((c) => (
              <CategoryTile key={c.id} category={c} />
            ))}
          </div>
        </ApiState>
      </section>
    </>
  );
}